import React, { useState, useRef } from 'react';
import styles from './ImageSearch.module.css';

const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

// Convert file to base64
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Ask Claude to identify the product in the image
async function identifyProduct(base64Image, mediaType) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 200,
      messages: [{
        role:    'user',
        content: [
          {
            type:   'image',
            source: { type: 'base64', media_type: mediaType, data: base64Image },
          },
          {
            type: 'text',
            text: `Look at this product image and return ONLY a short search query (5 words max) that would find this exact product on shopping sites like Amazon or Walmart.

Rules:
- Include brand name if visible
- Include model number if visible
- Include key spec (size, capacity, wattage) if visible
- Return ONLY the search query, nothing else — no punctuation, no explanation
- Examples: "Ninja AF101 Air Fryer 4Qt", "DeWalt 20V Cordless Drill DCD777", "KitchenAid Stand Mixer 5Qt"`,
          },
        ],
      }],
    }),
  });

  if (!response.ok) throw new Error(`Vision API error: ${response.status}`);
  const data = await response.json();
  return data.content?.find(b => b.type === 'text')?.text?.trim() || '';
}

export default function ImageSearch({ onQuery, disabled }) {
  const [preview,    setPreview]    = useState(null);
  const [analyzing,  setAnalyzing]  = useState(false);
  const [extracted,  setExtracted]  = useState('');
  const [error,      setError]      = useState('');
  const [dragOver,   setDragOver]   = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setError('');
    setExtracted('');

    // Show preview
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setAnalyzing(true);

    try {
      const base64    = await fileToBase64(file);
      const mediaType = file.type;
      const query     = await identifyProduct(base64, mediaType);

      if (!query) throw new Error('Could not identify product — try a clearer image');

      setExtracted(query);
      // Auto-trigger search with extracted query
      onQuery(query);
    } catch (err) {
      setError(err.message);
      setPreview(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleClear = () => {
    setPreview(null);
    setExtracted('');
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={styles.wrap}>

      {/* Upload trigger */}
      {!preview && !analyzing && (
        <div
          className={`${styles.dropzone} ${dragOver ? styles.dragOver : ''} ${disabled ? styles.disabled : ''}`}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <span className={styles.icon}>📸</span>
          <span className={styles.dropText}>
            {dragOver ? 'Drop image here' : 'Scan or upload a product'}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* Analyzing state */}
      {analyzing && (
        <div className={styles.analyzing}>
          <div className={styles.spinner} />
          <span>Identifying product…</span>
        </div>
      )}

      {/* Preview + result */}
      {preview && !analyzing && (
        <div className={styles.result}>
          <img src={preview} alt="Uploaded product" className={styles.preview} />
          <div className={styles.resultInfo}>
            {extracted && (
              <div className={styles.extractedQuery}>
                <span className={styles.extractedLabel}>Searching for:</span>
                <span className={styles.extractedText}>{extracted}</span>
              </div>
            )}
            <button className={styles.clearBtn} onClick={handleClear}>
              × Clear image
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={styles.error}>
          ⚠ {error}
          <button className={styles.errorClear} onClick={handleClear}>Dismiss</button>
        </div>
      )}

    </div>
  );
}
